import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LegalPageContainer,
  BackButton,
  LegalHighlightBox,
  LegalSection,
  ContactBox
} from '../../styles/legal/LegalShared.styles';
import { useSEO } from '../../hooks';

export const PrivacyPolicy: React.FC = () => {
  const navigate = useNavigate();

  useSEO({
    title: 'Privacy Policy | Plenty of Hearts',
    description: 'How Plenty of Hearts collects, uses, and protects your data.',
    canonical: '/privacy-policy',
  });

  return (
    <LegalPageContainer>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        <BackButton onClick={() => navigate('/')}>
          ← Back to App
        </BackButton>

        <h1>Privacy Policy</h1>

        <LegalHighlightBox>
          <p>Effective Date: September 23, 2025</p>
          <p>Last Updated: January 19, 2026</p>
        </LegalHighlightBox>

        <p>Welcome to Plenty of Hearts! We're committed to protecting your privacy and being transparent about how we
          collect, use, and share your information. This Privacy Policy explains our practices regarding your personal
          data when you use our personality-first dating app.</p>

        <LegalSection>
          <h2>1. Information We Collect</h2>
          <h3>1.1 Account Information</h3>
          <p>When you create an account, we collect:</p>
          <ul>
            <li><strong>Basic Profile Information:</strong> First name, last name, email address, phone number, date of birth</li>
            <li><strong>Location Data:</strong> City, state, country, and precise coordinates for matching purposes</li>
            <li><strong>Authentication Data:</strong> Email and phone verification status</li>
          </ul>

          <h3>1.2 Profile Content</h3>
          <p>To help you find meaningful connections, we collect:</p>
          <ul>
            <li><strong>Photos:</strong> Up to 5 profile photos you upload</li>
            <li><strong>Video Profiles:</strong> Video introductions that showcase your personality</li>
            <li><strong>Personality Data:</strong> Results from our personality assessments and compatibility quizzes</li>
          </ul>
        </LegalSection>

        <LegalSection>
          <h2>2. How We Use Your Information</h2>
          <p>We use your data to provide a premium, personality-focused dating experience:</p>
          <ul>
            <li><strong>Matching Algorithm:</strong> Suggesting compatible partners based on your "Heart Type"</li>
            <li><strong>AI Insights:</strong> Providing explanations for compatibility and conversation suggestions</li>
            <li><strong>Safety:</strong> Monitoring and removing inappropriate content to keep our community secure</li>
          </ul>
        </LegalSection>

        <LegalSection>
          <h2>3. Data Security</h2>
          <p>We implement industry-standard security measures to protect your information:</p>
          <ul>
            <li><strong>Encryption:</strong> All data is encrypted in transit and at rest</li>
            <li><strong>Access Controls:</strong> Strict limits on who can view personal information</li>
            <li><strong>Secure Infrastructure:</strong> We use enterprise-grade security for all database and authentication services</li>
          </ul>
        </LegalSection>

        <ContactBox>
          <h2>Questions about Privacy?</h2>
          <p>Our dedicated privacy team is here to help you understand how your data is protected.</p>
          <div className="methods">
            <div className="method">
              <span>Privacy Email</span>
              <a href="mailto:privacy@plentyofhearts.com">privacy@plentyofhearts.com</a>
            </div>
          </div>
        </ContactBox>

        <div style={{ textAlign: 'center', color: '#8e9aaf', fontSize: '0.9em', marginTop: '60px' }}>
          © 2026 Plenty of Hearts. Your privacy is our priority.
        </div>
      </div>
    </LegalPageContainer>
  );
};
